import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button, Input, message } from "antd";
import { ThumbsDown, ThumbsUp } from "@phosphor-icons/react";

import {
  createBriefFeedback,
  updateBriefFeedback,
} from "../../service/analysis";

/**
 * BriefFeedbackBar — attorney judgment capture on one Brief target
 * (a condition analysis or the strategic overview).
 *
 * Brief-improvement series (2026-07-23): every rating / comment /
 * correction lands in `BriefFeedback` on the backend, where it pairs
 * with the `BriefPromptCapture` row for the same target to form an
 * (input → output → judgment) training triple. The correction box —
 * "what should it have said" — is the highest-value signal: it yields
 * (context, rejected output, corrected output) preference pairs.
 *
 * Visibility is decided by the PARENT (`AnalysisReport`), which probes
 * the feedback list endpoint once per job; the backend 403s users not
 * on the rollout allowlist (limited to Matthew for now), so this bar
 * simply never renders for everyone else.
 *
 * One row per target: the parent hands us the current user's existing
 * feedback row (if any) and we PATCH it on subsequent edits instead of
 * stacking duplicates.
 */

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafbfc;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BarLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const SavedNote = styled.span`
  font-size: 12px;
  color: #059669;
`;

const RATING_UP = "up";
const RATING_DOWN = "down";
const RATING_NEUTRAL = "neutral";

const BriefFeedbackBar = ({
  jobId,
  targetKind,
  conditionAnalysisId,
  existing,
  onSaved,
}) => {
  const [rating, setRating] = useState(existing?.rating || RATING_NEUTRAL);
  const [comment, setComment] = useState(existing?.comment || "");
  const [correction, setCorrection] = useState(
    existing?.correction_text || ""
  );
  const [rowId, setRowId] = useState(existing?.id ?? null);
  const [formOpen, setFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const targetKey = `${jobId}:${targetKind}:${conditionAnalysisId ?? ""}`;
  const previousTargetKey = useRef(targetKey);

  // Re-sync for a new target, or when an existing row loads after the
  // component. Do not reset after this component creates/updates its own row:
  // thin API responses may omit the note fields the user just entered.
  useEffect(() => {
    const targetChanged = previousTargetKey.current !== targetKey;
    const loadedDifferentRow =
      existing?.id != null && existing.id !== rowId;
    if (!targetChanged && !loadedDifferentRow) return;

    previousTargetKey.current = targetKey;
    setRating(existing?.rating || RATING_NEUTRAL);
    setComment(existing?.comment || "");
    setCorrection(existing?.correction_text || "");
    setRowId(existing?.id ?? null);
    setFormOpen(false);
    setSavedAt(null);
  }, [existing, rowId, targetKey]);

  const persist = async (fields) => {
    setIsSaving(true);
    try {
      let response;
      if (rowId != null) {
        response = await updateBriefFeedback(rowId, fields);
      } else {
        response = await createBriefFeedback(jobId, {
          target_kind: targetKind,
          rating: fields.rating ?? rating ?? RATING_NEUTRAL,
          comment: fields.comment ?? comment ?? "",
          correction_text: fields.correction_text ?? correction ?? "",
          condition_analysis_id:
            targetKind === "condition_analysis" ? conditionAnalysisId : null,
        });
      }
      const row = response?.data || null;
      if (row?.id != null) setRowId(row.id);
      setSavedAt(Date.now());
      if (typeof onSaved === "function" && row) onSaved(row);
      return true;
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Couldn't save your feedback.";
      // eslint-disable-next-line no-console
      console.error("[brief-feedback] save failed:", err);
      message.error(detail, 6);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleThumb = async (value) => {
    const previous = rating;
    setRating(value);
    const ok = await persist({ rating: value });
    if (!ok) setRating(previous);
  };

  const handleSaveNote = async () => {
    const ok = await persist({
      rating,
      comment,
      correction_text: correction,
    });
    if (ok) {
      setFormOpen(false);
      message.success(
        "Feedback recorded — it feeds the Brief training corpus.",
        4
      );
    }
  };

  return (
    <Bar data-testid="brief-feedback-bar" data-target-kind={targetKind}>
      <BarRow>
        <BarLabel>Was this analysis right?</BarLabel>
        <Button
          size="small"
          type={rating === RATING_UP ? "primary" : "default"}
          icon={<ThumbsUp size={14} weight={rating === RATING_UP ? "fill" : "regular"} />}
          onClick={() => handleThumb(RATING_UP)}
          disabled={isSaving}
          data-testid="brief-feedback-thumb-up"
          aria-label="Thumbs up"
        />
        <Button
          size="small"
          danger={rating === RATING_DOWN}
          type={rating === RATING_DOWN ? "primary" : "default"}
          icon={
            <ThumbsDown
              size={14}
              weight={rating === RATING_DOWN ? "fill" : "regular"}
            />
          }
          onClick={() => handleThumb(RATING_DOWN)}
          disabled={isSaving}
          data-testid="brief-feedback-thumb-down"
          aria-label="Thumbs down"
        />
        <Button
          size="small"
          type="link"
          onClick={() => setFormOpen((open) => !open)}
          data-testid="brief-feedback-expand"
        >
          {formOpen
            ? "Hide"
            : comment || correction
            ? "Edit note / correction"
            : "Add note / correction"}
        </Button>
        {savedAt != null && !formOpen && (
          <SavedNote data-testid="brief-feedback-saved">Saved</SavedNote>
        )}
      </BarRow>
      {formOpen && (
        <>
          <div>
            <FieldLabel htmlFor={`bf-comment-${targetKind}-${conditionAnalysisId ?? "overview"}`}>
              What was wrong (or right)?
            </FieldLabel>
            <Input.TextArea
              id={`bf-comment-${targetKind}-${conditionAnalysisId ?? "overview"}`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="e.g. The nexus section missed the June 2019 private opinion."
              data-testid="brief-feedback-comment"
            />
          </div>
          <div>
            <FieldLabel htmlFor={`bf-correction-${targetKind}-${conditionAnalysisId ?? "overview"}`}>
              What should it have said? (optional — trains future Briefs)
            </FieldLabel>
            <Input.TextArea
              id={`bf-correction-${targetKind}-${conditionAnalysisId ?? "overview"}`}
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 10 }}
              placeholder="Rewrite the analysis (or the part of it) the way an expert attorney would have written it."
              data-testid="brief-feedback-correction"
            />
          </div>
          <BarRow>
            <Button
              size="small"
              type="primary"
              onClick={handleSaveNote}
              loading={isSaving}
              data-testid="brief-feedback-save"
            >
              Save feedback
            </Button>
            <Button
              size="small"
              onClick={() => setFormOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </BarRow>
        </>
      )}
    </Bar>
  );
};

BriefFeedbackBar.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  targetKind: PropTypes.oneOf(["condition_analysis", "strategic_overview"])
    .isRequired,
  conditionAnalysisId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  existing: PropTypes.shape({
    id: PropTypes.number,
    rating: PropTypes.string,
    comment: PropTypes.string,
    correction_text: PropTypes.string,
  }),
  onSaved: PropTypes.func,
};

BriefFeedbackBar.defaultProps = {
  conditionAnalysisId: null,
  existing: null,
  onSaved: null,
};

export default BriefFeedbackBar;
